<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from '@/i18n'
import { useAuthStore } from '@/stores/auth'
import { paymentsApi } from '@/api'

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()

const loading = ref(false)
const loadingSubscription = ref(false)
const credits = ref(10)
const inputCredits = ref('10')

const PRICE_PER_CREDIT = 3.95
const DIRECTOR_PRICE = 9.95
const MAX_SLIDER = 120
const MAX_INPUT = 720

const isAuthenticated = computed(() => authStore.isAuthenticated)
const isDirector = computed(() => authStore.isDirector)
const subscriptionStatus = computed(() => authStore.subscriptionStatus)

const isOverSliderMax = computed(() => credits.value > MAX_SLIDER)

const totalPrice = computed(() => (credits.value * PRICE_PER_CREDIT).toFixed(2))

const videoDuration = computed(() => {
  const totalSeconds = credits.value * 30
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  
  if (hours > 0) {
    return `${hours}h ${minutes}min ${seconds}s`
  } else if (minutes > 0) {
    return `${minutes}min ${seconds}s`
  }
  return `${seconds}s`
})

function onSliderChange(value: number) {
  credits.value = value
  inputCredits.value = String(value)
}

function onInputChange() {
  const value = parseInt(inputCredits.value) || 1
  const clampedValue = Math.min(Math.max(value, 1), MAX_INPUT)
  credits.value = clampedValue
  inputCredits.value = String(clampedValue)
}

function onInputBlur() {
  onInputChange()
}

const includedFeatures = [
  { icon: '✨', key: 'script' },
  { icon: '🖼️', key: 'images' },
  { icon: '🎥', key: 'video' },
  { icon: '🎵', key: 'audio' },
  { icon: '⬇️', key: 'download' },
  { icon: '📐', key: 'formats' },
  { icon: '🚫', key: 'no_watermark' },
  { icon: '🎬', key: 'intro_video' }
]

const directorFeatures = [
  { icon: '📦', key: 'download_assets' },
  { icon: '📝', key: 'script_json' },
  { icon: '🖼️', key: 'all_images' },
  { icon: '🎬', key: 'all_videos' },
  { icon: '📁', key: 'zip_archive' }
]

async function buyCredits() {
  if (!isAuthenticated.value) {
    router.push('/login?redirect=/pricing')
    return
  }
  
  loading.value = true
  try {
    const response = await paymentsApi.createCheckout(credits.value)
    window.location.href = response.data.checkout_url
  } catch (error) {
    console.error('Failed to create checkout:', error)
  } finally {
    loading.value = false
  }
}

async function subscribeDirector() {
  if (!isAuthenticated.value) {
    router.push('/login?redirect=/pricing')
    return
  }
  
  loadingSubscription.value = true
  try {
    const checkoutUrl = await authStore.subscribeDirector()
    if (checkoutUrl) {
      window.location.href = checkoutUrl
    }
  } catch (error) {
    console.error('Failed to create subscription checkout:', error)
  } finally {
    loadingSubscription.value = false
  }
}
</script>

<template>
  <div class="pricing-view">
    <div class="pricing-header">
      <h1>{{ t('pricing.title') }}</h1>
      <p>{{ t('pricing.subtitle') }}</p>
    </div>

    <div class="pricing-info">
      <div class="info-card highlight">
        <span class="info-icon">💎</span>
        <div class="info-content">
          <strong>1 {{ t('pricing.credit') }}</strong>
          <span>= {{ t('pricing.duration') }}</span>
        </div>
      </div>
      <div class="info-card">
        <span class="info-icon">💰</span>
        <div class="info-content">
          <strong>{{ PRICE_PER_CREDIT }} CHF</strong>
          <span>{{ t('pricing.per_credit') }}</span>
        </div>
      </div>
    </div>

    <!-- Credit Explanation -->
    <div class="credit-explanation">
      <div class="explanation-card">
        <h3>{{ t('pricing.how.title') }}</h3>
        <p>{{ t('pricing.how.desc') }}</p>
        <p class="example">{{ t('pricing.example') }}</p>
      </div>
    </div>

    <!-- Credit Selector -->
    <div class="credit-selector">
      <div class="selector-card">
        <h2>{{ t('pricing.select_credits') }}</h2>
        
        <!-- Input + Display -->
        <div class="credits-display">
          <div class="credits-input-wrapper">
            <input
              v-model="inputCredits"
              type="number"
              min="1"
              :max="MAX_INPUT"
              class="credits-input"
              @change="onInputChange"
              @blur="onInputBlur"
            />
            <span class="credits-label">{{ credits > 1 ? t('pricing.credits') : t('pricing.credit') }}</span>
          </div>
          
          <div class="equals">=</div>
          
          <div class="price-display">
            <span class="price-amount">{{ totalPrice }} CHF</span>
          </div>
        </div>
        
        <!-- Duration info -->
        <div class="duration-info">
          <span class="duration-icon">🎬</span>
          <span>{{ videoDuration }} {{ t('pricing.of_video') }}</span>
        </div>
        
        <!-- Slider -->
        <div class="slider-wrapper" :class="{ disabled: isOverSliderMax }">
          <el-slider
            :model-value="Math.min(credits, MAX_SLIDER)"
            :min="1"
            :max="MAX_SLIDER"
            :disabled="isOverSliderMax"
            :show-tooltip="false"
            @update:model-value="onSliderChange"
          />
          <div class="slider-labels">
            <span>1</span>
            <span>{{ MAX_SLIDER }}</span>
          </div>
        </div>
        
        <!-- Over max message -->
        <div v-if="isOverSliderMax" class="over-max-message">
          <span class="warning-icon">⚠️</span>
          {{ t('pricing.over_slider_max', { max: MAX_SLIDER }) }}
        </div>
        
        <!-- Buy button -->
        <el-button
          type="primary"
          size="large"
          :loading="loading"
          @click="buyCredits"
          class="buy-btn"
        >
          {{ t('pricing.buy') }} {{ credits }} {{ credits > 1 ? t('pricing.credits') : t('pricing.credit') }}
        </el-button>
      </div>
    </div>

    <div class="pricing-features">
      <h2>{{ t('pricing.included.title') }}</h2>
      <div class="features-grid">
        <div v-for="feature in includedFeatures" :key="feature.key" class="feature">
          <span class="feature-icon">{{ feature.icon }}</span>
          <span class="feature-text">{{ t(`pricing.included.${feature.key}`) }}</span>
        </div>
      </div>
    </div>

    <!-- Director Subscription -->
    <div class="director-section">
      <div class="director-card" :class="{ active: isDirector }">
        <div class="director-badge" v-if="isDirector">
          <span>✓</span> {{ t('pricing.director.active') }}
        </div>
        <div class="director-header">
          <div class="director-icon">🎬</div>
          <h2>{{ t('pricing.director.title') }}</h2>
          <p class="director-desc">{{ t('pricing.director.description') }}</p>
        </div>
        
        <div class="director-price">
          <span class="price-value">{{ DIRECTOR_PRICE }}</span>
          <span class="price-currency">CHF</span>
          <span class="price-period">/ {{ t('pricing.director.month') }}</span>
        </div>
        
        <div class="director-features">
          <div v-for="feature in directorFeatures" :key="feature.key" class="director-feature">
            <span class="check-icon">✓</span>
            <span>{{ t(`pricing.director.features.${feature.key}`) }}</span>
          </div>
        </div>
        
        <el-button
          v-if="!isDirector"
          type="primary"
          size="large"
          :loading="loadingSubscription"
          @click="subscribeDirector"
          class="subscribe-btn"
        >
          {{ t('pricing.director.subscribe') }}
        </el-button>
        
        <div v-else class="subscription-active">
          <p v-if="subscriptionStatus === 'canceling'" class="canceling-notice">
            {{ t('pricing.director.canceling') }}
          </p>
          <router-link to="/profile" class="manage-link">
            {{ t('pricing.director.manage') }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pricing-view {
  min-height: calc(100vh - 64px);
  padding: var(--m2v-space-12) var(--m2v-space-6);
}

.pricing-header {
  text-align: center;
  margin-bottom: var(--m2v-space-10);
}

.pricing-header h1 {
  font-family: var(--m2v-font-display);
  font-size: 2.5rem;
  margin-bottom: var(--m2v-space-4);
}

.pricing-header p {
  color: var(--m2v-text-muted);
  font-size: 1.125rem;
}

.pricing-info {
  display: flex;
  justify-content: center;
  gap: var(--m2v-space-8);
  margin-bottom: var(--m2v-space-8);
  flex-wrap: wrap;
}

.info-card {
  display: flex;
  align-items: center;
  gap: var(--m2v-space-3);
  padding: var(--m2v-space-4) var(--m2v-space-6);
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-lg);
  transition: all var(--m2v-transition-normal);
}

.info-card.highlight {
  border-color: var(--m2v-primary);
  background: var(--m2v-gradient-card);
}

.info-icon {
  font-size: 2rem;
}

.info-content {
  display: flex;
  flex-direction: column;
}

.info-content strong {
  font-size: 1.125rem;
}

.info-content span {
  color: var(--m2v-text-muted);
  font-size: 0.875rem;
}

/* Credit Explanation */
.credit-explanation {
  max-width: 700px;
  margin: 0 auto var(--m2v-space-10);
}

.explanation-card {
  padding: var(--m2v-space-6);
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-lg);
  text-align: center;
}

.explanation-card h3 {
  font-size: 1.25rem;
  margin-bottom: var(--m2v-space-4);
  color: var(--m2v-text-primary);
}

.explanation-card p {
  color: var(--m2v-text-secondary);
  line-height: 1.6;
  margin-bottom: var(--m2v-space-3);
}

.explanation-card .example {
  color: var(--m2v-primary-light);
  font-weight: 500;
  margin-bottom: 0;
}

/* Credit Selector */
.credit-selector {
  max-width: 600px;
  margin: 0 auto var(--m2v-space-12);
}

.selector-card {
  padding: var(--m2v-space-10);
  background: var(--m2v-bg-surface);
  border: 2px solid var(--m2v-primary);
  border-radius: var(--m2v-radius-xl);
  text-align: center;
  box-shadow: 0 0 40px rgba(99, 102, 241, 0.15);
}

.selector-card h2 {
  font-size: 1.5rem;
  margin-bottom: var(--m2v-space-8);
}

.credits-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--m2v-space-6);
  margin-bottom: var(--m2v-space-6);
  flex-wrap: wrap;
}

.credits-input-wrapper {
  display: flex;
  align-items: center;
  gap: var(--m2v-space-3);
}

.credits-input {
  width: 120px;
  padding: var(--m2v-space-4);
  font-family: var(--m2v-font-display);
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
  background: var(--m2v-bg-elevated);
  border: 2px solid var(--m2v-border-default);
  border-radius: var(--m2v-radius-lg);
  color: var(--m2v-text-primary);
  transition: all var(--m2v-transition-fast);
}

.credits-input:focus {
  outline: none;
  border-color: var(--m2v-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
}

.credits-input::-webkit-inner-spin-button,
.credits-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.credits-input[type=number] {
  -moz-appearance: textfield;
}

.credits-label {
  color: var(--m2v-text-muted);
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.equals {
  font-size: 2rem;
  color: var(--m2v-text-muted);
}

.price-display {
  background: var(--m2v-gradient-hero);
  padding: var(--m2v-space-4) var(--m2v-space-6);
  border-radius: var(--m2v-radius-lg);
}

.price-amount {
  font-family: var(--m2v-font-display);
  font-size: 2rem;
  font-weight: 700;
  color: white;
}

.duration-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--m2v-space-2);
  margin-bottom: var(--m2v-space-8);
  padding: var(--m2v-space-3) var(--m2v-space-6);
  background: var(--m2v-bg-elevated);
  border-radius: var(--m2v-radius-full);
  color: var(--m2v-text-secondary);
  font-size: 1rem;
  display: inline-flex;
}

.duration-icon {
  font-size: 1.25rem;
}

.slider-wrapper {
  margin-bottom: var(--m2v-space-6);
  padding: 0 var(--m2v-space-4);
}

.slider-wrapper.disabled {
  opacity: 0.5;
}

.slider-wrapper :deep(.el-slider__runway) {
  height: 8px;
  background: var(--m2v-bg-elevated);
  border-radius: var(--m2v-radius-full);
}

.slider-wrapper :deep(.el-slider__bar) {
  height: 8px;
  background: var(--m2v-gradient-hero);
  border-radius: var(--m2v-radius-full);
}

.slider-wrapper :deep(.el-slider__button) {
  width: 24px;
  height: 24px;
  border: 3px solid var(--m2v-primary);
  background: var(--m2v-bg-surface);
}

.slider-wrapper :deep(.el-slider__button:hover) {
  transform: scale(1.1);
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  margin-top: var(--m2v-space-2);
  color: var(--m2v-text-muted);
  font-size: 0.875rem;
}

.over-max-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--m2v-space-2);
  padding: var(--m2v-space-3) var(--m2v-space-4);
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: var(--m2v-radius-md);
  color: var(--m2v-warning);
  font-size: 0.875rem;
  margin-bottom: var(--m2v-space-6);
}

.warning-icon {
  font-size: 1.125rem;
}

.buy-btn {
  width: 100%;
  height: 56px;
  font-size: 1.125rem;
  font-weight: 600;
}

/* Features */
.pricing-features {
  max-width: 900px;
  margin: 0 auto var(--m2v-space-12);
  text-align: center;
}

.pricing-features h2 {
  font-size: 1.5rem;
  margin-bottom: var(--m2v-space-8);
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--m2v-space-4);
}

.feature {
  display: flex;
  align-items: center;
  gap: var(--m2v-space-3);
  padding: var(--m2v-space-4);
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-md);
  transition: all var(--m2v-transition-fast);
}

.feature:hover {
  border-color: var(--m2v-primary);
}

.feature-icon {
  font-size: 1.5rem;
}

.feature-text {
  color: var(--m2v-text-secondary);
  font-size: 0.9rem;
}

/* Director Section */
.director-section {
  max-width: 600px;
  margin: 0 auto;
}

.director-card {
  position: relative;
  padding: var(--m2v-space-10);
  background: var(--m2v-bg-surface);
  border: 2px solid var(--m2v-border-default);
  border-radius: var(--m2v-radius-xl);
  text-align: center;
  transition: all var(--m2v-transition-normal);
}

.director-card:hover {
  border-color: #f59e0b;
}

.director-card.active {
  border-color: #10b981;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%);
}

.director-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  padding: var(--m2v-space-2) var(--m2v-space-4);
  background: #10b981;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: var(--m2v-radius-full);
  display: flex;
  align-items: center;
  gap: var(--m2v-space-2);
}

.director-header {
  margin-bottom: var(--m2v-space-6);
}

.director-icon {
  font-size: 3rem;
  margin-bottom: var(--m2v-space-3);
}

.director-header h2 {
  font-family: var(--m2v-font-display);
  font-size: 1.75rem;
  margin-bottom: var(--m2v-space-2);
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.director-desc {
  color: var(--m2v-text-muted);
  font-size: 1rem;
}

.director-price {
  margin-bottom: var(--m2v-space-6);
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: var(--m2v-space-1);
}

.price-value {
  font-family: var(--m2v-font-display);
  font-size: 3rem;
  font-weight: 700;
  color: var(--m2v-text-primary);
}

.price-currency {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--m2v-text-secondary);
}

.price-period {
  color: var(--m2v-text-muted);
  font-size: 1rem;
}

.director-features {
  text-align: left;
  margin-bottom: var(--m2v-space-8);
  padding: var(--m2v-space-6);
  background: var(--m2v-bg-elevated);
  border-radius: var(--m2v-radius-lg);
}

.director-feature {
  display: flex;
  align-items: center;
  gap: var(--m2v-space-3);
  padding: var(--m2v-space-2) 0;
  color: var(--m2v-text-secondary);
}

.check-icon {
  color: #10b981;
  font-weight: 700;
}

.subscribe-btn {
  width: 100%;
  height: 56px;
  font-size: 1.125rem;
  font-weight: 600;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border: none;
}

.subscribe-btn:hover {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
}

.subscription-active {
  padding: var(--m2v-space-4);
}

.canceling-notice {
  color: var(--m2v-warning);
  font-size: 0.875rem;
  margin-bottom: var(--m2v-space-3);
}

.manage-link {
  color: var(--m2v-primary-light);
  font-weight: 500;
  text-decoration: none;
}

.manage-link:hover {
  text-decoration: underline;
}

@media (max-width: 640px) {
  .credits-display {
    flex-direction: column;
    gap: var(--m2v-space-4);
  }
  
  .equals {
    display: none;
  }
  
  .credits-input {
    width: 100%;
    max-width: 200px;
  }
}
</style>
